import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Heart, Sparkles, Award, MapPin, Palette, BookOpen } from 'lucide-react';

const AboutSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section id="about" className="py-24 relative" ref={sectionRef} data-testid="about-section">
      {/* Background Accent */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1/3 h-[500px] bg-gradient-to-r from-[#D4AF37]/5 to-transparent pointer-events-none" />

      <div className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="font-manrope text-sm text-[#D4AF37] uppercase tracking-widest mb-4 block">
              About Us
            </span>

            <h2 className="font-cinzel text-section-title text-gold-gradient mb-6">
              Ethereal Calligraphy
            </h2>

            <div className="w-20 h-0.5 bg-gradient-to-r from-[#AA8C2C] to-[#F4C430] mb-8" />

            <div className="space-y-6">
              <p className="font-manrope text-gray-300 leading-relaxed text-lg">
                We create handcrafted Islamic calligraphy paintings that blend spirituality with modern artistic expression. Each artwork carries deep meaning from Quranic verses and is designed to bring peace, beauty, and inspiration into homes.
              </p>

              <p className="font-manrope text-gray-400 leading-relaxed">
                Based in Hyderabad, Art Create Valley's mission is to make meaningful Islamic art accessible locally. Every piece is created with devotion, using premium materials and traditional techniques passed down through generations.
              </p>

              <p className="font-manrope text-gray-400 leading-relaxed">
                Each painting is not just decoration—it's a window to spiritual contemplation, a daily reminder of divine beauty, and a legacy piece for your family.
              </p>
            </div>

            {/* Features */}
            <div className="grid grid-cols-3 gap-6 mt-10">
              <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3 }}
              >
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <Heart className="text-[#D4AF37]" size={20} />
                </div>
                <span className="font-manrope text-sm text-gray-400">Handcrafted</span>
              </motion.div>

              <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.4 }}
              >
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <Sparkles className="text-[#D4AF37]" size={20} />
                </div>
                <span className="font-manrope text-sm text-gray-400">Unique</span>
              </motion.div>

              <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5 }}
              >
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <Award className="text-[#D4AF37]" size={20} />
                </div>
                <span className="font-manrope text-sm text-gray-400">Premium</span>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Visual - Decorative Element instead of placeholder image */}
          <motion.div
            className="relative flex items-center justify-center"
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="relative">
              {/* Decorative rings */}
              <motion.div 
                className="absolute inset-0 rounded-full border border-[#D4AF37]/20"
                style={{ transform: 'scale(1.3)' }}
                animate={{ rotate: 360 }}
                transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
              />
              <motion.div 
                className="absolute inset-0 rounded-full border border-[#D4AF37]/10"
                style={{ transform: 'scale(1.5)' }}
                animate={{ rotate: -360 }}
                transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
              />

              {/* Main decorative card */}
              <div className="glass rounded-2xl w-72 h-96 md:w-80 md:h-[420px] flex flex-col items-center justify-center text-center p-8 relative overflow-hidden">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/5 via-transparent to-[#D4AF37]/5" />
                
                {/* Content */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: 0.5 }}
                  className="relative z-10"
                >
                  {/* Arabic Calligraphy */}
                  <p className="font-amiri text-4xl md:text-5xl text-[#D4AF37] mb-6">
                    بِسْمِ اللَّهِ
                  </p>
                  
                  {/* Brand Name */}
                  <p className="font-cinzel text-xl text-white mb-2">
                    Ethereal Calligraphy
                  </p>
                  <p className="font-manrope text-sm text-[#D4AF37]/70 mb-4">
                    Art Create Valley
                  </p>
                  
                  {/* Location */}
                  <div className="flex items-center justify-center gap-2 text-gray-400">
                    <MapPin size={14} />
                    <span className="font-manrope text-sm">Hyderabad</span>
                  </div>

                  {/* Stats */}
                  <div className="mt-8 pt-6 border-t border-[#D4AF37]/20 grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Palette size={14} className="text-[#D4AF37]" />
                      </div>
                      <span className="font-cinzel text-2xl text-[#D4AF37] block">50+</span>
                      <span className="font-manrope text-xs text-gray-500">Artworks</span>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <BookOpen size={14} className="text-[#D4AF37]" />
                      </div>
                      <span className="font-cinzel text-2xl text-[#D4AF37] block">100+</span>
                      <span className="font-manrope text-xs text-gray-500">Happy Clients</span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
